import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { Button } from "./ui/button";
import { API, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const PLANS = [
  { lookup_key: "pamind_monthly", name: "Monthly", price: "£7.99", period: "/month",
    note: "Flexible. Cancel anytime.", features: ["Full question bank access", "Quiz & test modes", "Progress tracking", "Detailed explanations"] },
  { lookup_key: "pamind_6month", name: "6 Months", price: "£40", period: "/6 months",
    note: "Save £7.94 (16.6%) — equivalent to getting 1 month free", features: ["Everything in Monthly", "6 months prepaid access", "Progress kept permanently"] },
  { lookup_key: "pamind_annual", name: "12 Months", price: "£60", period: "/year",
    note: "BEST VALUE — Save £35.88 (37.4%)", best: true, features: ["Everything in Monthly", "12 months prepaid access", "Progress kept permanently", "Go into every exam feeling confident and fully prepared"] },
];

export function PricingCards() {
  const { user, hasAccess } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const choose = async (plan) => {
    if (!user) { navigate(`/register?plan=${plan.lookup_key}`); return; }
    if (hasAccess) { navigate("/app"); return; }
    setLoading(plan.lookup_key);
    try {
      const { data } = await API.post("/payments/checkout", {
        lookup_key: plan.lookup_key, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setLoading(null);
    }
  };

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="pricing-cards">
      {PLANS.map((p) => (
        <div key={p.lookup_key} data-testid={`plan-card-${p.lookup_key}`}
          className={`relative bg-card rounded-lg border p-7 flex flex-col shadow-sm transition-shadow duration-200 hover:shadow-md ${
            p.best ? "border-primary ring-2 ring-primary" : "border-border"}`}>
          {p.best && (
            <span className="absolute -top-3 left-6 bg-primary text-primary-foreground text-xs font-bold tracking-wide px-3 py-1 rounded-full" data-testid="best-value-badge">
              BEST VALUE
            </span>
          )}
          <h3 className="font-display font-semibold text-lg">{p.name}</h3>
          <div className="mt-3 flex items-baseline gap-1">
            <span className="font-display text-4xl font-extrabold tracking-tight">{p.price}</span>
            <span className="text-sm text-muted-foreground">{p.period}</span>
          </div>
          <p className={`mt-2 text-xs font-semibold ${p.best ? "text-success" : "text-muted-foreground"}`}>{p.note}</p>
          <ul className="mt-5 space-y-2.5 flex-1">
            {p.features.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                <Check className="w-4 h-4 text-success shrink-0 mt-0.5" /> {f}
              </li>
            ))}
          </ul>
          <Button data-testid={`plan-select-${p.lookup_key}`} onClick={() => choose(p)}
            disabled={loading === p.lookup_key}
            variant={p.best ? "default" : "outline"} className="mt-6 w-full">
            {loading === p.lookup_key ? "Redirecting…" : hasAccess ? "Go to dashboard" : `Choose ${p.name}`}
          </Button>
        </div>
      ))}
    </div>
  );
}
